/*
 * StackSchematic overlay: the sign-in page.
 *
 * TWO MODES, CHOSEN BY CONFIG — `stackschematic.signIn.provider`
 *
 *   'google'  a real sign-in screen. Nothing in the app renders until the
 *             visitor authenticates with Google. This is what a public
 *             deployment gets.
 *   'guest'   sign in silently as a guest, no click-through. Local development,
 *             where a Google round trip per reload is friction with no benefit.
 *
 * The default is 'guest', because a developer running `yarn start` has no OAuth
 * client configured and should not be blocked by one. Production opts IN, in
 * app-config.stackschematic-prod.yaml. A deployment that forgets to set it gets
 * the safe-to-run option, not a broken login.
 *
 * WHY THE GUEST MODE EXISTS AT ALL
 *
 * The scaffolded app configures `auth.providers.guest`, so the app plugin's
 * default sign-in page (`sign-in-page:app`, rendering `<SignInPage
 * providers={['guest']} />`) gates every load with an "Enter as a Guest User"
 * card. With one provider and no real identity there is nothing to choose.
 *
 * Disabling the extension in config removes the gate - and the identity with it.
 * That broke TechDocs, which fetches its static content through a cookie minted
 * from the user's credentials: GET /api/techdocs/.backstage/auth/v1/cookie
 * answered 401, and so did /api/notifications/status and /api/search/query.
 *
 * `ProxiedSignInPage` is the public component for exactly this shape - a sign-in
 * page "that has no user interface of its own". It punches through to the auth
 * backend for the named provider and hands back an identity.
 *
 * WHY GUEST IS NOT ACCEPTABLE IN PRODUCTION
 *
 * It signs EVERYONE in, with no identity. On a public origin that is anonymous
 * read access to the whole estate, which is worse than the click-through it was
 * written to remove - the card at least implies a boundary exists.
 *
 * WHO GOOGLE LETS IN IS NOT DECIDED HERE
 *
 * Completing a Google login proves only which mailbox someone controls. The
 * decision about which mailboxes are allowed is a sign-in RESOLVER question,
 * made in app-config: `emailMatchingUserEntityProfileEmail` admits exactly the
 * addresses that appear on a User entity in the catalog. Nothing in this file
 * grants access, and it must not - a frontend cannot enforce authorization.
 *
 * WHY THE GUEST PATH RETRIES
 *
 * `ProxiedSignInPage` attempts the exchange exactly once, on mount, and renders
 * an error panel if it fails - "You do not appear to be signed in. Please try
 * reloading the browser page." There is no retry, so a single transient failure
 * is permanent until the human reloads.
 *
 * In dev that failure is close to guaranteed. `yarn start` serves the frontend in
 * about 25 seconds and the backend takes up to three minutes on a cold start, so
 * anyone who opens the page in that window asks an auth backend that is not
 * listening yet, and is then told they are not signed in for as long as they
 * leave the tab open.
 *
 * Remounting on a changing `key` re-runs the mount effect, so the wrapper below
 * retries with backoff instead of dead-ending. It is bounded: after the budget is
 * exhausted the default error panel is shown, because a genuinely misconfigured
 * auth provider must still surface rather than spin forever.
 *
 * The Google path needs none of this: a human is looking at a button, and
 * clicking it again is its own retry.
 *
 * OVERRIDING RATHER THAN ADDING
 *
 * This extension deliberately has NO `name`. `resolveExtensionDefinition` builds
 * an id as `kind:namespace/name`, or `kind:namespace` when there is no name, and
 * the namespace is the module's `pluginId`. So this resolves to `sign-in-page:app`
 * - the same id as the default - and `resolveAppNodeSpecs` REPLACES a plugin
 * extension with a module extension of the same id. Give it a name and the id
 * becomes `sign-in-page:app/<name>`, which attaches alongside the default instead
 * and does nothing. Only `root` is forbidden from being overridden.
 */
import { useEffect, useState } from 'react';
import { ProxiedSignInPage, Progress, SignInPage } from '@backstage/core-components';
import { configApiRef, googleAuthApiRef, useApi } from '@backstage/core-plugin-api';
import type { SignInPageProps } from '@backstage/plugin-app-react';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';

/**
 * Roughly three minutes of patience, which is about how long a cold `yarn start`
 * backend takes. Past that, stop guessing and show the error.
 */
const MAX_ATTEMPTS = 40;
const MAX_DELAY_MS = 5_000;

/** 250ms doubling to a 5s ceiling: quick when the backend is nearly up, calm when it is not. */
const delayFor = (attempt: number) => Math.min(250 * 2 ** attempt, MAX_DELAY_MS);

const GOOGLE_PROVIDER = {
  id: 'google-auth-provider',
  title: 'Google',
  message: 'Sign in to StackSchematic with your Google account',
  apiRef: googleAuthApiRef,
};

/**
 * Shown while waiting to try again. Deliberately just a progress bar: during a
 * cold start this is indistinguishable to the user from the app still loading,
 * which is what it actually is.
 */
function RetryingSignIn(props: { attempt: number; onRetry: () => void }) {
  const { attempt, onRetry } = props;
  useEffect(() => {
    const timer = setTimeout(onRetry, delayFor(attempt));
    return () => clearTimeout(timer);
  }, [attempt, onRetry]);
  return <Progress />;
}

function AutoGuestSignIn(props: SignInPageProps) {
  const [attempt, setAttempt] = useState(0);
  const exhausted = attempt >= MAX_ATTEMPTS;

  return (
    <ProxiedSignInPage
      {...props}
      // Changing the key remounts the component, which re-runs its mount effect.
      // This is the whole retry mechanism; ProxiedSignInPage exposes no other way
      // to ask it to try again.
      key={attempt}
      provider="guest"
      // Omitting ErrorComponent falls through to the default error panel, which is
      // what should happen once the budget is spent.
      ErrorComponent={
        exhausted
          ? undefined
          : () => <RetryingSignIn attempt={attempt} onRetry={() => setAttempt(a => a + 1)} />
      }
    />
  );
}

function StackSchematicSignIn(props: SignInPageProps) {
  const config = useApi(configApiRef);
  // Read at render rather than at module load: the config API is not available
  // until the app is running, and a top-level read would capture nothing.
  const provider = config.getOptionalString('stackschematic.signIn.provider') ?? 'guest';

  if (provider === 'google') {
    // No `auto`: that fires the popup on load, which reads as the site breaking
    // rather than as a login. A visitor should see a page that says what this is
    // and offers one button.
    return <SignInPage {...props} providers={[GOOGLE_PROVIDER]} title="StackSchematic" />;
  }
  return <AutoGuestSignIn {...props} />;
}

export const autoGuestSignInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => (props: SignInPageProps) => <StackSchematicSignIn {...props} />,
  },
});
